import React from "react";
import { connect } from "react-redux";
import { Link } from 'react-router-dom';
import PropTypes from 'prop-types';
import _ from 'lodash';

import { Col, Grid, Row, Table } from "react-bootstrap";
import Card from "components/Card/Card.jsx";
import Button from "components/CustomButton/CustomButton.jsx";
import { getProducts, setAutocompleteProducts, softDeleteProduct, getFilterProducts } from "../../../redux/actions/product";
import ProductGeneralAutosuggest1 from "../../../app-components/Products/ProductGeneralAutosuggest/ProductGenerateAutosuggest1.jsx";
import DeleteConfirmationModal from "components/DeleteConfirmationModal/DeleteConfirmationModal.jsx";
import { withAnalysisTracking } from '../../../components/Statistics/AnalysisTracker';
import "./EquivalenceForm.css";

class EquivalenceForm extends React.Component {
    constructor(props) {
        super(props);
        this.renderProduct = this.renderProduct.bind(this);
        this.state = {
            nom: '',
            DCI: '',
            classesPharmacologiques: '',
            familleTherapeutique: '',
            dosage: '',
            forme: '',
            id: '',
            searchCache: new Map(),
            lastQuery: '',
            showDeleteModal: false,
            productToDelete: null
        };

        this.debounceGetProducts = _.debounce(this.handleSearch, 300);

        // Bind des méthodes
        this.handleSearch = this.handleSearch.bind(this);
        this.setValue = this.setValue.bind(this);
        this.handleDeleteClick = this.handleDeleteClick.bind(this);
        this.handleDeleteConfirm = this.handleDeleteConfirm.bind(this);
        this.handleDeleteCancel = this.handleDeleteCancel.bind(this);
        this.searchEquivalences = this.searchEquivalences.bind(this);
    }

    handleSearch(filters) {
        const searchKey = JSON.stringify(filters);

        if (this.state.searchCache.has(searchKey)) {
            const cachedResults = this.state.searchCache.get(searchKey);
            this.props.setAutocompleteProducts(cachedResults);
            return;
        }

        if (filters.nom && filters.nom.length < 2) {
            this.props.setAutocompleteProducts([]);
            return;
        }

        try {
            this.props.getProducts(filters, true).then(() => {
                if (this.props.autocompleteProducts) {
                    this.state.searchCache.set(searchKey, this.props.autocompleteProducts);

                    if (this.state.searchCache.size > 50) {
                        const firstKey = this.state.searchCache.keys().next().value;
                        this.state.searchCache.delete(firstKey);
                    }
                }
            });
        } catch (error) {

        }
    }

    setValue(val, index) {
        let newState = {};
        newState[index] = val;
        this.setState(newState);

        if (index === 'nom') {
            this.debounceGetProducts({
                nom: val,
                DCI: this.state.DCI,
                forme: this.state.forme,
                limit: 20
            });
        }
    }

    handleDeleteClick(product) {
        this.setState({
            showDeleteModal: true,
            productToDelete: product
        });
    }

    handleDeleteConfirm() {
        if (this.state.productToDelete) {
            this.props.softDeleteProduct(this.state.productToDelete._id);
        }
        this.setState({
            showDeleteModal: false,
            productToDelete: null
        });
    }

    handleDeleteCancel() {
        this.setState({
            showDeleteModal: false,
            productToDelete: null
        });
    }

    searchEquivalences() {
        if (!this.state.id || !this.state.nom) {
            if (this.props.showNotification) {
                this.props.showNotification({
                    message: 'Veuillez d\'abord sélectionner un médicament',
                    level: 'warning'
                });
            }
            return;
        }

        const productData = {
            _id: this.state.id,
            nom: this.state.nom,
            DCI: this.state.DCI,
            forme: this.state.forme,
            dosage: this.state.dosage,
            familleTherapeutique: this.state.familleTherapeutique,
            classesPharmacologiques: this.state.classesPharmacologiques
        };

        return this.props.getFilterProducts(productData).then(async (results) => {
            // Track the analysis for statistics
            try {
                // Prepare analysis data
                const analysisData = {
                    type: 'THERAPEUTIC_EQUIVALENCE',
                    medications: [this.state.nom],
                    findingsCount: results && results.length > 0 ? results.length : 0,
                    findings: [],
                    riskLevel: 'LOW',
                    patientContext: [],
                    responseTime: 0,
                    cacheHit: false
                };

                if (this.props.onAnalysisComplete) {
                    const result = await this.props.onAnalysisComplete(analysisData);

                    if (result && result.analysisId) {
                        await this.props.onSaveAnalysis(result.analysisId, {
                            name: `Recherche d'équivalences`,
                            notes: `Recherche d'équivalences pour ${this.state.nom}`
                        });

                        await this.props.onViewAnalysis(result.analysisId, {
                            timeSpentSeconds: 5,
                            action: 'VIEW_DETAILS'
                        });
                    }
                }
            } catch (trackingError) {}

            if (results && results.length > 0) {
                if (this.props.showNotification) {
                    this.props.showNotification({
                        message: `${results.length} équivalence(s) trouvée(s) pour "${this.state.nom}"`,
                        level: 'success'
                    });
                }
            } else {
                if (this.props.showNotification) {
                    this.props.showNotification({
                        message: `Aucune équivalence trouvée pour "${this.state.nom}". Vérifiez les critères de recherche.`,
                        level: 'info'
                    });
                }
            }

            return results;
        }).catch((error) => {

            if (this.props.showNotification) {
                this.props.showNotification({
                    message: 'Erreur lors de la recherche d\'équivalences: ' + (error.message || 'Erreur inconnue'),
                    level: 'error'
                });
            }
        });
    }

    getEquivalenceBadge(prod) {
        // Récupérer le produit de référence (celui recherché)
        const refDCI = this.state.DCI;
        const refForme = this.state.forme;
        const refDosage = this.state.dosage;

        // Normaliser les valeurs pour la comparaison
        const normalizeDCI = (dci) => (dci || '').toString().trim().toLowerCase();
        const normalizeForme = (forme) => (forme || '').toString().trim().toLowerCase();
        const normalizeDosage = (dosage) => (dosage || '').toString().trim().toLowerCase();

        const prodDCI = normalizeDCI(prod.DCI);
        const prodForme = normalizeForme(prod.forme);
        const prodDosage = normalizeDosage(prod.dosage);

        const searchDCI = normalizeDCI(refDCI);
        const searchForme = normalizeForme(refForme);
        const searchDosage = normalizeDosage(refDosage);

        // Badge EXACT : même DCI + même forme + même dosage
        if (prodDCI === searchDCI && prodForme === searchForme && prodDosage === searchDosage) {
            return {
                label: 'EXACT',
                color: '#28a745',
                bgColor: 'rgba(40, 167, 69, 0.1)',
                icon: 'fa-check-circle'
            };
        }

        // Badge PROCHE : même DCI + même forme + dosage différent
        if (prodDCI === searchDCI && prodForme === searchForme && prodDosage !== searchDosage) {
            return {
                label: 'PROCHE',
                color: '#ff9800',
                bgColor: 'rgba(255, 152, 0, 0.1)',
                icon: 'fa-adjust'
            };
        }

        // Badge ALTERNATIF : même DCI + forme différente et/ou dosage différent
        if (prodDCI === searchDCI && (prodForme !== searchForme || prodDosage !== searchDosage)) {
            return {
                label: 'ALTERNATIF',
                color: 'var(--app-primary-color)',
                bgColor: 'var(--app-primary-tint)',
                icon: 'fa-exchange-alt'
            };
        }

        // Aucun badge si les critères ne correspondent pas
        return null;
    }

    renderProduct() {
        if (this.props.loading) {
            return (
                <tr>
                    <td colSpan={6} style={{
                        padding: '40px 24px',
                        textAlign: 'center',
                        color: '#666',
                        fontSize: '16px'
                    }}>
                        <div style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: '12px'
                        }}>
                            <i className="fa fa-spinner fa-spin" style={{
                                fontSize: '20px',
                                color: 'var(--app-primary-color)'
                            }} />
                            Recherche d équivalences en cours...
                        </div>
                    </td>
                </tr>
            );
        }

        if (this.props.equivalences && this.props.equivalences.length > 0) {

            return this.props.equivalences.map((prod, key) => {
                const badge = this.getEquivalenceBadge(prod);
                return (
                    <tr key={key} style={{
                        transition: 'all 0.3s ease',
                        cursor: 'pointer',
                        borderLeft: `4px solid #e9ecef` // Bordure neutre
                    }}
                    onMouseEnter={(e) => {
                        e.currentTarget.style.backgroundColor = '#f8fafc';
                        e.currentTarget.style.transform = 'translateX(2px)';
                    }}
                    onMouseLeave={(e) => {
                        e.currentTarget.style.backgroundColor = '';
                        e.currentTarget.style.transform = 'translateX(0)';
                    }}>
                        <td style={{
                            padding: '18px 24px',
                            fontWeight: '600',
                            fontSize: '15px',
                            color: '#2c3e50'
                        }}>
                            <div style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '12px'
                            }}>
                                <span style={{
                                    fontWeight: '700',
                                    flexGrow: 1
                                }}>
                                    {prod.nom}
                                </span>
                            </div>
                            {prod.fabricant && prod.fabricant !== 'Non spécifié' && (
                                <div style={{
                                    fontSize: '12px',
                                    color: '#6c757d',
                                    marginTop: '4px',
                                    fontStyle: 'italic'
                                }}>
                                    {prod.fabricant}
                                </div>
                            )}
                        </td>
                    <td style={{
                        padding: '18px 24px',
                        fontSize: '14px',
                        color: '#5a6c7d'
                    }}>
                        {prod.DCI}
                    </td>
                    <td style={{
                        padding: '18px 24px',
                        fontSize: '14px',
                        color: '#5a6c7d'
                    }}>
                        {prod.forme}
                    </td>
                    <td style={{
                        padding: '18px 24px',
                        fontSize: '14px',
                        color: '#5a6c7d',
                        fontWeight: '500'
                    }}>
                        {prod.dosage}
                    </td>
                    <td style={{
                        padding: '18px 24px',
                        textAlign: 'center'
                    }}>
                        {badge ? (
                            <span style={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: '6px',
                                padding: '8px 14px',
                                borderRadius: '20px',
                                fontSize: '12px',
                                fontWeight: '700',
                                textTransform: 'uppercase',
                                letterSpacing: '0.5px',
                                color: badge.color,
                                whiteSpace: 'nowrap'
                            }}>
                                <i className={`fa ${badge.icon}`} style={{ fontSize: '13px' }}></i>
                                {badge.label}
                            </span>
                        ) : (
                            <span style={{
                                fontSize: '13px',
                                color: '#999',
                                fontStyle: 'italic'
                            }}>-</span>
                        )}
                    </td>
                    {this.props.profile && (this.props.profile.role === 'super' || this.props.profile.role === 'admin') ? (
                        <td style={{
                            padding: '18px 24px',
                            textAlign: 'center'
                        }}>
                            <div style={{
                                display: 'flex',
                                flexDirection : 'row',
                                gap: '8px'
                            }}>
                                {prod._id || prod.id ? (
                                    <Link to={`/app/product/detail/${prod._id || prod.id}`}>
                                        <Button
                                            simple
                                            bsStyle="info"
                                            title="Voir les détails"
                                            style={{
                                                borderRadius: '6px',
                                                padding: '8px 16px',
                                                fontSize: '13px',
                                                fontWeight: '500'
                                            }}
                                        >
                                            <i className="fa fa-eye" style={{ marginRight: '6px' }} />
                                            Détail
                                        </Button>
                                    </Link>
                                ) : (
                                    <Button
                                        simple
                                        bsStyle="default"
                                        disabled
                                        title="ID produit manquant"
                                        style={{
                                            borderRadius: '6px',
                                            padding: '8px 16px',
                                            fontSize: '13px',
                                            fontWeight: '500',
                                            opacity: 0.5
                                        }}
                                    >
                                        <i className="fa fa-eye" style={{ marginRight: '6px' }} />
                                        Détail (ID manquant)
                                    </Button>
                                )}
                                {prod._id || prod.id ? (
                                    <Link to={`/app/product/form/${prod._id || prod.id}`}>
                                        <Button
                                            simple
                                            bsStyle="warning"
                                            title="Modifier"
                                            style={{
                                                borderRadius: '6px',
                                                padding: '8px 16px',
                                                fontSize: '13px',
                                                fontWeight: '500'
                                            }}
                                        >
                                            <i className="fa fa-edit" style={{ marginRight: '6px' }} />
                                            Modifier
                                        </Button>
                                    </Link>
                                ) : (
                                    <Button
                                        simple
                                        bsStyle="default"
                                        disabled
                                        title="ID produit manquant"
                                        style={{
                                            borderRadius: '6px',
                                            padding: '8px 16px',
                                            fontSize: '13px',
                                            fontWeight: '500',
                                            opacity: 0.5
                                        }}
                                    >
                                        <i className="fa fa-edit" style={{ marginRight: '6px' }} />
                                        Modifier (ID manquant)
                                    </Button>
                                )}
                                <Button
                                    bsStyle="danger"
                                    fill
                                    onClick={() => this.handleDeleteClick(prod)}
                                    title="Supprimer"
                                    style={{
                                        borderRadius: '6px',
                                        padding: '8px 16px',
                                        fontSize: '13px',
                                        fontWeight: '500'
                                    }}
                                >
                                    <i className="fa fa-trash" style={{ marginRight: '6px' }}/>
                                    Supprimer
                                </Button>
                            </div>
                        </td>
                    ) : null}
                </tr>
            );
        });
        } else {
            return (
                <tr>
                    <td colSpan={6} style={{
                        padding: '40px 24px',
                        textAlign: 'center',
                        color: '#999',
                        fontSize: '16px',
                        fontStyle: 'italic'
                    }}>
                        <div style={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            gap: '12px'
                        }}>
                            <i className="fa fa-search" style={{
                                fontSize: '32px',
                                color: '#ccc'
                            }}></i>
                            <div>
                                <div style={{ marginBottom: '8px', fontWeight: '500' }}>
                                    Aucune équivalence trouvée
                                </div>
                                <small style={{ color: '#aaa' }}>
                                    Essayez de rechercher avec un autre médicament
                                </small>
                            </div>
                        </div>
                    </td>
                </tr>
            );
        }
    }

    render() {
        if (!this.props.profile) {
            return <div>Chargement du profil...</div>;
        }

        return (
            <div className="main-content equivalence-page">
                <Grid fluid>
                    <Row>
                        <Col sm={12}>
                            <Card
                                title="Choisir un médicament"
                                category="Sélectionnez un médicament de référence pour trouver ses équivalences."
                                content={
                                <Grid fluid>
                                    <Row className="equivalence-search-row">
                                        <Col sm={12} md={8}>
                                            <div className="equivalence-search-field">
                                                <label htmlFor="equivalence-product-search">Médicament de référence</label>
                                            <ProductGeneralAutosuggest1
                                                id="equivalence-product-search"
                                                value={this.state.nom}
                                                onChange={(e) => this.setValue(e.target.value, 'nom')}
                                                addTag={(val) => {
                                                    if (this.props.onChangeProduct) {
                                                        this.props.onChangeProduct(val);
                                                    }
                                                    this.setState({
                                                        nom: val.nom,
                                                        id: val._id,
                                                        forme: val.forme,
                                                        dosage: val.dosage,
                                                        DCI: val.DCI,
                                                        familleTherapeutique: val.familleTherapeutique,
                                                        classesPharmacologiques: val.classesPharmacologiques
                                                    }, () => {
                                                        setTimeout(() => {
                                                            this.searchEquivalences();
                                                        }, 100);
                                                    });
                                                }}
                                                suggestions={this.props.autocompleteProducts}
                                                placeholder="Rechercher un médicament..."
                                                clearSuggestions={() => this.props.setAutocompleteProducts([])}
                                                getSuggestions={(val) => this.debounceGetProducts({
                                                    nom: val,
                                                    limit: 20
                                                })}
                                                style={{
                                                    width: '100%',
                                                    height: '66px',
                                                    padding: '18px 20px',
                                                    fontSize: '18px',
                                                    borderRadius: '8px'
                                                }}
                                                formControl
                                                nameColumn="nom"
                                                pillColumns={['forme', 'dosage']}
                                            />
                                            </div>
                                        </Col>
                                        <Col sm={12} md={4} className="equivalence-search-action">
                                            <Button
                                                bsStyle="info"
                                                fill
                                                type="button"
                                                disabled={this.props.loading || !this.state.id}
                                                className="equivalence-search-button"
                                                style={{
                                                    backgroundColor: 'var(--app-primary-color)',
                                                    borderColor: 'var(--app-primary-color)',
                                                    color: '#fff'
                                                }}
                                                onClick={this.searchEquivalences}
                                                aria-label="Rechercher les équivalences du médicament sélectionné"
                                            >
                                                {this.props.loading ? (
                                                    <span>
                                                        <i className="fa fa-spinner fa-spin" /> Recherche en cours...
                                                    </span>
                                                ) : (
                                                    <span>
                                                        <i className="fa fa-search" /> Rechercher les Équivalences
                                                    </span>
                                                )}
                                            </Button>
                                        </Col>
                                    </Row>
                                    {this.state.id && (
                                        <div className="equivalence-reference">
                                            <span className="equivalence-reference-icon"><i className="fa fa-pills" /></span>
                                            <div>
                                                <span className="equivalence-reference-label">Médicament sélectionné</span>
                                                <strong>{this.state.nom}</strong>
                                            </div>
                                            <div className="equivalence-reference-details">
                                                {this.state.DCI && <span>{this.state.DCI}</span>}
                                                {this.state.forme && <span>{this.state.forme}</span>}
                                                {this.state.dosage && <span>{this.state.dosage}</span>}
                                            </div>
                                        </div>
                                    )}
                                </Grid>
                            } />
                        </Col>
                    </Row>

                    {(this.state.id || this.props.loading || (this.props.equivalences && this.props.equivalences.length > 0)) && (
                    <Row>
                        <Col sm={12}>
                            <Card
                                tableFullWidth
                                content={
                                    <div className="equivalence-results-table" style={{
                                        background: 'white',
                                        borderRadius: '8px',
                                        overflow: 'hidden',
                                        boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                                        border: '1px solid #e0e0e0'
                                    }}>
                                        <div className="equivalence-results-header" style={{
                                            backgroundColor: 'var(--app-primary-color)',
                                            padding: '16px 24px',
                                            color: 'white'
                                        }}>
                                            <h4 style={{
                                                margin: 0,
                                                fontSize: '16px',
                                                fontWeight: '600',
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: '10px'
                                            }}>
                                                <i className="fa fa-list-alt" style={{ fontSize: '18px' }}></i>
                                                Équivalences trouvées
                                                {this.props.equivalences && this.props.equivalences.length > 0 && (
                                                    <span style={{
                                                        backgroundColor: 'rgba(255,255,255,0.2)',
                                                        padding: '4px 12px',
                                                        borderRadius: '12px',
                                                        fontSize: '14px',
                                                        fontWeight: '500'
                                                    }}>
                                                        {this.props.equivalences.length} résultat{this.props.equivalences.length > 1 ? 's' : ''}
                                                    </span>
                                                )}
                                            </h4>
                                        </div>

                                        <Table
                                            hover
                                            responsive
                                            style={{
                                                margin: 0,
                                                borderCollapse: 'separate',
                                                borderSpacing: 0
                                            }}
                                        >
                                            <thead>
                                                <tr style={{
                                                    backgroundColor: '#f8f9fa'
                                                }}>
                                                    <th style={{
                                                        padding: '16px 24px',
                                                        fontWeight: '700',
                                                        fontSize: '14px',
                                                        color: 'var(--app-primary-color)',
                                                        textTransform: 'uppercase',
                                                        letterSpacing: '0.5px',
                                                        borderBottom: '2px solid var(--app-primary-color)',
                                                        width: '25%'
                                                    }}>
                                                        <i className="fa fa-tag" style={{ marginRight: '8px', color: 'var(--app-primary-color)' }}></i>
                                                        Nom commercial
                                                    </th>
                                                    <th style={{
                                                        padding: '16px 24px',
                                                        fontWeight: '700',
                                                        fontSize: '14px',
                                                        color: 'var(--app-primary-color)',
                                                        textTransform: 'uppercase',
                                                        letterSpacing: '0.5px',
                                                        borderBottom: '2px solid var(--app-primary-color)',
                                                        width: '20%'
                                                    }}>
                                                        <i className="fa fa-flask" style={{ marginRight: '8px', color: 'var(--app-primary-color)' }}></i>
                                                        DCI
                                                    </th>
                                                    <th style={{
                                                        padding: '16px 24px',
                                                        fontWeight: '700',
                                                        fontSize: '14px',
                                                        color: 'var(--app-primary-color)',
                                                        textTransform: 'uppercase',
                                                        letterSpacing: '0.5px',
                                                        borderBottom: '2px solid var(--app-primary-color)',
                                                        width: '15%'
                                                    }}>
                                                        <i className="fa fa-capsules" style={{ marginRight: '8px', color: 'var(--app-primary-color)' }}></i>
                                                        Forme
                                                    </th>
                                                    <th style={{
                                                        padding: '16px 24px',
                                                        fontWeight: '700',
                                                        fontSize: '14px',
                                                        color: 'var(--app-primary-color)',
                                                        textTransform: 'uppercase',
                                                        letterSpacing: '0.5px',
                                                        borderBottom: '2px solid var(--app-primary-color)',
                                                        width: '15%'
                                                    }}>
                                                        <i className="fa fa-prescription-bottle-alt" style={{ marginRight: '8px', color: 'var(--app-primary-color)' }}></i>
                                                        Dosage
                                                    </th>
                                                    <th style={{
                                                        padding: '16px 24px',
                                                        fontWeight: '700',
                                                        fontSize: '14px',
                                                        color: 'var(--app-primary-color)',
                                                        textTransform: 'uppercase',
                                                        letterSpacing: '0.5px',
                                                        borderBottom: '2px solid var(--app-primary-color)',
                                                        width: '15%',
                                                        textAlign: 'center'
                                                    }}>
                                                        <i className="fa fa-certificate" style={{ marginRight: '8px', color: 'var(--app-primary-color)' }}></i>
                                                        Équivalence
                                                    </th>
                                                    {this.props.profile && (this.props.profile.role === 'super' || this.props.profile.role === 'admin') ? (
                                                        <th style={{
                                                            padding: '16px 24px',
                                                            fontWeight: '700',
                                                            fontSize: '14px',
                                                            color: 'var(--app-primary-color)',
                                                            textTransform: 'uppercase',
                                                            letterSpacing: '0.5px',
                                                            borderBottom: '2px solid var(--app-primary-color)',
                                                            textAlign: 'center',
                                                            width: '10%'
                                                        }}>
                                                            <i className="fa fa-tools" style={{ marginRight: '8px', color: 'var(--app-primary-color)' }}></i>
                                                            Actions
                                                        </th>
                                                    ) : null}
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {this.renderProduct()}
                                            </tbody>
                                        </Table>
                                    </div>
                                }
                            />
                        </Col>
                    </Row>
                    )}
                </Grid>

                <DeleteConfirmationModal
                    show={this.state.showDeleteModal}
                    onConfirm={this.handleDeleteConfirm}
                    onCancel={this.handleDeleteCancel}
                    itemName={this.state.productToDelete ? `le produit "${this.state.productToDelete.nom}"` : ''}
                />
            </div>
        );
    }
}

const mapStateToProps = state => ({
    products: state.products.products,
    loading: state.status.loading,
    equivalences: state.products.equivalences,
    autocompleteProducts: state.products.autocompleteProducts,
    profile: state.user.profile
});

const mapDispatchToProps = dispatch => ({
    getProducts: (filter, autocomplete) => dispatch(getProducts(filter, autocomplete)),
    setAutocompleteProducts: products => dispatch(setAutocompleteProducts(products)),
    softDeleteProduct: productId => dispatch(softDeleteProduct(productId)),
    getFilterProducts: product => dispatch(getFilterProducts(product))
});

const ConnectedEquivalenceForm = connect(mapStateToProps, mapDispatchToProps)(EquivalenceForm);
export default withAnalysisTracking(ConnectedEquivalenceForm);
